import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { debounce } from '@theme/utilities';

// Same on-brand arrows used by the product-image carousel (white via currentColor).
const ARROW_LEFT = `<svg viewBox="0 0 48 51" width="16" height="17" aria-hidden="true" fill="currentColor"><path d="M24.2247 0L0 25.512L8.29018 34.3575L24.1571 51C24.9844 50.5359 25.7837 50.0424 26.5667 49.5172L13.8806 30.9669L11.3172 29.5648C11.3172 29.5648 46.4697 26.8362 47.9984 26.7263C47.996 26.2304 48.003 24.5766 47.9984 24.0954C46.4883 24.0001 11.3685 21.4401 11.3685 21.4401L13.8782 20.0355L26.6133 1.48031C25.8419 0.95512 25.0427 0.464125 24.2247 0Z" /></svg>`;
const ARROW_RIGHT = `<svg viewBox="0 0 48 51" width="16" height="17" aria-hidden="true" fill="currentColor"><path d="M23.7753 0L48 25.512L39.7098 34.3575L23.8429 51C23.0156 50.5359 22.2163 50.0424 21.4333 49.5172L34.1194 30.9669L36.6828 29.5648C36.6828 29.5648 1.53031 26.8362 0.001641 26.7263C0.00397129 26.2304 -0.00301958 24.5766 0.001641 24.0954C1.51167 24.0001 36.6315 21.4401 36.6315 21.4401L34.1218 20.0355L21.3867 1.48031C22.1581 0.95512 22.9573 0.464125 23.7753 0Z" /></svg>`;

/**
 * Solios: "Complete your look" recommendations, driven by a Rebuy data source.
 *
 * Fetches Rebuy's data-source API for the CURRENT variant, renders cards in the
 * theme's format (image / title / price / add-to-cart), and re-fetches when the
 * shopper switches variant. Add-to-cart reuses the same AJAX + CartUpdateEvent
 * flow as the card-add-to-cart block so the drawer/bag update identically.
 *
 * Config comes from data attributes set in blocks/rebuy-recommendations.liquid.
 *
 * @extends Component
 */
class RebuyRecommendations extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.grid = this.querySelector('.rebuy-recs__grid');

    // Cart mode: base the recommendations on the cart's contents (the watch)
    // and refresh whenever the cart changes.
    if (this.dataset.cartMode === 'true') {
      this.#loadFromCart();
      document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
      return;
    }

    const variantId = this.#resolveVariantId();
    if (variantId) this.dataset.variantId = variantId;
    this.#load(variantId);

    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /** Resolve the recommendation variant from the cart (the first watch), then load. */
  async #loadFromCart() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const cart = await (await fetch(`${root}cart.js`)).json();
      const items = cart.items || [];
      // Don't recommend a strap the shopper already has in the cart.
      this._cartVariantIds = new Set(items.map((i) => String(i.variant_id)));
      // Prefer a watch as the basis; fall back to the first line item.
      const basis = items.find((i) => /watch/i.test(i.product_type || '')) || items[0];
      const variantId = basis?.variant_id;
      if (!variantId) {
        this.#render([]);
        return;
      }
      this.dataset.variantId = String(variantId);
      await this.#load(variantId);
    } catch (error) {
      console.error('[rebuy-recommendations] cart load failed:', error);
      this.#render([]);
    }
  }

  // Reload on any cart change (debounced). Reloading only fetches — it never
  // adds to the cart — so there's no feedback loop.
  #onCartUpdate = () => {
    if (this._reloadTimer) clearTimeout(this._reloadTimer);
    this._reloadTimer = setTimeout(() => this.#loadFromCart(), 250);
  };

  /**
   * The current variant id. The Liquid `product` global can be blank inside
   * nested blocks, so fall back to the live product form / URL.
   */
  #resolveVariantId() {
    const fromLiquid = this.dataset.variantId;
    if (fromLiquid && fromLiquid !== '0' && fromLiquid !== '') return fromLiquid;

    const fromForm = document.querySelector('product-form-component [name="id"], form[action*="/cart/add"] [name="id"]');
    if (fromForm?.value) return fromForm.value;

    const fromUrl = new URLSearchParams(location.search).get('variant');
    if (fromUrl) return fromUrl;

    const checked = document.querySelector('variant-picker input[data-current-checked="true"]');
    return checked?.dataset?.variantId || '';
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  #onVariantUpdate = (event) => {
    const id = event.detail?.resource?.id;
    if (id && String(id) !== this.dataset.variantId) {
      this.dataset.variantId = String(id);
      this.#load(id);
    }
  };

  async #load(variantId) {
    const { key, source, limit, locale } = this.dataset;
    if (!key || !source || !variantId || !this.grid) return;

    const url =
      `https://rebuyengine.com/api/v1/custom/id/${encodeURIComponent(source)}` +
      `?key=${encodeURIComponent(key)}` +
      `&shopify_variant_ids=${encodeURIComponent(variantId)}` +
      `&limit=${encodeURIComponent(limit || 6)}` +
      (locale ? `&locale=${encodeURIComponent(locale)}` : '');

    try {
      const response = await fetch(url);
      const json = await response.json();
      let base = (json.data || []).map(normalize).filter(Boolean);
      // In the cart, hide anything the shopper already added.
      if (this._cartVariantIds) {
        base = base.filter((item) => !this._cartVariantIds.has(String(item.variantId)));
      }
      // Rebuy returns the shop's base price/currency only, so pull each item's
      // presentment price from Shopify (which respects the buyer's market).
      const items = await Promise.all(base.map((item) => this.#enrichPrice(item)));
      this.#render(items);
    } catch (error) {
      console.error('[rebuy-recommendations] fetch failed:', error);
      this.#render([]);
    }
  }

  /** Overwrite price/availability with Shopify's market-aware presentment price. */
  async #enrichPrice(item) {
    if (!item.handle) return item;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}products/${item.handle}.js`);
      if (!response.ok) return item;
      const product = await response.json();
      const variant =
        product.variants.find((v) => String(v.id) === String(item.variantId)) || product.variants[0];
      if (variant) {
        item.priceCents = variant.price; // cents, in the current market currency
        item.available = variant.available;
      }
    } catch (error) {
      // Keep Rebuy's price as a fallback.
    }
    return item;
  }

  #render(items) {
    // No recommendations for this variant → hide the block so it leaves no gap.
    this.hidden = items.length === 0;
    if (!items.length) {
      this.innerHTML = '';
      return;
    }

    const perView = Number(this.dataset.cols) || 2;
    const cards = items.map((item) => this.#cardHTML(item)).join('');

    this.innerHTML = `
      <div class="rebuy-recs__carousel${items.length > perView ? '' : ' rebuy-recs--no-nav'}">
        <button type="button" class="rebuy-recs__arrow rebuy-recs__arrow--prev" aria-label="Previous">${ARROW_LEFT}</button>
        <div class="rebuy-recs__viewport">
          <div class="rebuy-recs__track">${cards}</div>
        </div>
        <button type="button" class="rebuy-recs__arrow rebuy-recs__arrow--next" aria-label="Next">${ARROW_RIGHT}</button>
      </div>`;

    this.track = this.querySelector('.rebuy-recs__track');
    this.prevButton = this.querySelector('.rebuy-recs__arrow--prev');
    this.nextButton = this.querySelector('.rebuy-recs__arrow--next');

    this.prevButton.addEventListener('click', () => this.#page(-1));
    this.nextButton.addEventListener('click', () => this.#page(1));
    this.track.addEventListener('scroll', this.#onScroll, { passive: true });

    for (const form of this.querySelectorAll('.rebuy-card__form')) {
      form.addEventListener('submit', this.#addToCart);
    }

    this.#updateArrows();
  }

  /** Scroll to the previous/next card (robust for peek layouts + any width). */
  #page(direction) {
    if (!this.track) return;
    const cards = Array.from(this.track.children);
    if (cards.length < 2) return;
    const scrollLeft = this.track.scrollLeft;
    // Current card = the one whose left edge is nearest the current scroll.
    let index = 0;
    let best = Infinity;
    cards.forEach((card, i) => {
      const distance = Math.abs(card.offsetLeft - scrollLeft);
      if (distance < best) {
        best = distance;
        index = i;
      }
    });
    const target = Math.min(Math.max(index + direction, 0), cards.length - 1);
    this.track.scrollTo({ left: cards[target].offsetLeft, behavior: 'smooth' });
  }

  #onScroll = debounce(() => this.#updateArrows(), 60);

  #updateArrows() {
    if (!this.track || !this.prevButton || !this.nextButton) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    this.prevButton.disabled = scrollLeft <= 2;
    this.nextButton.disabled = scrollLeft + clientWidth >= scrollWidth - 2;
  }

  #cardHTML(item) {
    const label = this.dataset.buttonLabel || 'Add to cart';
    const soldOut = this.dataset.soldoutLabel || 'Sold out';
    const price = item.priceCents != null ? formatMoney(item.priceCents) : formatPrice(item.price);

    return `
      <div class="rebuy-card">
        <a class="rebuy-card__media" href="${item.url}" aria-label="${escapeHtml(item.title)}">
          ${item.image ? `<img src="${item.image}" alt="${escapeHtml(item.title)}" loading="lazy">` : ''}
        </a>
        <a class="rebuy-card__title" href="${item.url}">${escapeHtml(item.title)}</a>
        <div class="rebuy-card__price">${price}</div>
        <form class="rebuy-card__form" data-variant-id="${item.variantId}">
          <button type="submit" class="rebuy-card__btn"${item.available ? '' : ' disabled'}>
            <span class="rebuy-card__btn-label">${escapeHtml(item.available ? label : soldOut)}</span>
          </button>
        </form>
      </div>`;
  }

  #addToCart = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const card = form.closest('.rebuy-card');
    const label = form.querySelector('.rebuy-card__btn-label');
    const original = label.innerHTML;

    card.dataset.state = 'loading';
    label.textContent = 'Adding...';

    const root = window.Shopify?.routes?.root || '/';

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(form.dataset.variantId), quantity: 1 }] }),
      });
      if (!response.ok) throw new Error(await response.text());

      label.textContent = 'Added';
      await this.#notifyTheme(root);
      setTimeout(() => {
        label.innerHTML = original;
        card.dataset.state = '';
      }, 1500);
    } catch (error) {
      console.error('[rebuy-recommendations] add to cart failed:', error);
      label.textContent = 'Try again';
      card.dataset.state = '';
      setTimeout(() => (label.innerHTML = original), 2000);
    }
  };

  // Mirrors card-add-to-cart: refresh the bag count and pop the drawer via the
  // theme's own CartUpdateEvent.
  async #notifyTheme(root) {
    try {
      const { CartUpdateEvent } = await import('@theme/events');
      const cart = await (await fetch(`${root}cart.js`)).json();
      document.dispatchEvent(
        new CartUpdateEvent(cart, 'rebuy-recommendations', {
          itemCount: cart.item_count,
          source: 'rebuy-recommendations',
          sections: {},
        })
      );
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer?.hasAttribute('auto-open')) drawer.open();
    } catch (error) {
      window.location.reload();
    }
  }
}

/**
 * Map a Rebuy product to the fields the card needs.
 * @param {any} product
 */
function normalize(product) {
  const variant = (product.variants || [])[0];
  if (!variant) return null;

  const src = product.image?.src || (product.images && product.images[0]?.src) || '';
  const image = src ? `${src}${src.includes('?') ? '&' : '?'}width=600` : '';

  return {
    title: product.title || '',
    handle: product.handle,
    url: `/products/${product.handle}?variant=${variant.id}`,
    image,
    variantId: variant.id,
    price: variant.price,
    available: isAvailable(variant),
  };
}

/** Format cents in the buyer's active market currency + locale. */
function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'CAD';
  const locale = document.documentElement.lang || 'en';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  } catch (error) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

/** @param {any} variant */
function isAvailable(variant) {
  if (variant.available != null) return Boolean(variant.available);
  if (variant.inventory_management == null) return true; // inventory not tracked
  if (variant.inventory_policy === 'continue') return true; // oversell allowed
  return (variant.inventory_quantity ?? 0) > 0;
}

/** Rebuy returns price as a dollar string, e.g. "70.00". */
function formatPrice(value) {
  const n = Number(value);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : `$${value}`;
}

function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, (c) => {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
  });
}

if (!customElements.get('rebuy-recommendations')) {
  customElements.define('rebuy-recommendations', RebuyRecommendations);
}
